import { Controller } from "@hotwired/stimulus"
import { trackEvent, operationEvent } from "./analytics"
import { currentLocale, translate, type Locale } from "../shared/localization"
import {
  createEmptySession,
  isOperation,
  type ConversionWarning,
  type Diagnostic,
  type InputFormat,
  type JsonOutputStyle,
  type Operation,
  type OutputFormat,
  type ProcessingResponse,
  type RouteMode,
  type WorkbenchModeConfig,
  type WorkbenchSession,
} from "./types"

const SAMPLE_JSON = JSON.stringify(
  {
    service: "formatloom",
    enabled: true,
    ports: [3000, 3001],
  },
  null,
  2,
)

const SAMPLE_YAML = "service: formatloom\nenabled: true\nports:\n  - 3000\n  - 3001\n"

export function isCurrentRequest(
  currentRequestId: string | null,
  responseRequestId: string,
): boolean {
  return currentRequestId !== null && currentRequestId === responseRequestId
}

export default class extends Controller<HTMLElement> {
  static targets = [
    "input",
    "output",
    "status",
    "diagnostics",
    "warnings",
    "inputBytes",
    "runButton",
    "copyButton",
    "downloadButton",
    "resetButton",
    "sampleButton",
    "operationButton",
    "operationLabel",
    "outputStyle",
    "formatHint",
    "validationResult",
    "copyStatus",
  ]

  static values = {
    mode: String,
    operation: String,
    inputFormat: String,
    outputFormat: String,
    workerSrc: String,
  }

  declare readonly inputTarget: HTMLTextAreaElement
  declare readonly outputTarget: HTMLTextAreaElement
  declare readonly statusTarget: HTMLElement
  declare readonly diagnosticsTarget: HTMLElement
  declare readonly warningsTarget: HTMLElement
  declare readonly inputBytesTarget: HTMLElement
  declare readonly runButtonTarget: HTMLButtonElement
  declare readonly copyButtonTarget: HTMLButtonElement
  declare readonly downloadButtonTarget: HTMLButtonElement
  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly sampleButtonTarget: HTMLButtonElement
  declare readonly operationButtonTargets: HTMLElement[]
  declare readonly operationLabelTarget: HTMLElement
  declare readonly outputStyleTarget: HTMLSelectElement
  declare readonly hasOutputStyleTarget: boolean
  declare readonly formatHintTarget: HTMLElement
  declare readonly validationResultTarget: HTMLElement
  declare readonly copyStatusTarget: HTMLElement
  declare modeValue: string
  declare operationValue: string
  declare inputFormatValue: string
  declare outputFormatValue: string
  declare workerSrcValue: string

  private worker: Worker | null = null
  private requestId: string | null = null
  private session!: WorkbenchSession

  private readonly handleTurboBeforeCache = (): void => {
    this.resetForNavigation()
  }

  private readonly handlePageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      this.resetForNavigation()
    }
  }

  private readonly handleLocaleChange = (): void => {
    this.render()
  }

  connect(): void {
    const config = this.modeConfig()
    this.session = createEmptySession(config)
    this.worker = this.createWorker()
    this.render()
    document.addEventListener("turbo:before-cache", this.handleTurboBeforeCache)
    window.addEventListener("pageshow", this.handlePageShow)
    document.addEventListener("formatloom:locale-change", this.handleLocaleChange)
  }

  disconnect(): void {
    document.removeEventListener("turbo:before-cache", this.handleTurboBeforeCache)
    window.removeEventListener("pageshow", this.handlePageShow)
    document.removeEventListener("formatloom:locale-change", this.handleLocaleChange)
    this.requestId = null
    this.worker?.terminate()
    this.worker = null
  }

  private resetForNavigation(): void {
    this.requestId = null
    this.session = createEmptySession(this.modeConfig())
    this.render()
  }

  inputChanged(): void {
    this.session.inputText = this.inputTarget.value
    this.session.outputText = null
    this.session.diagnostics = []
    this.session.warnings = []
    this.session.status = this.session.inputText.trim() ? "editing" : "empty"
    this.session.isBusy = false
    this.requestId = null
    this.render()
  }

  selectOperation(event: Event): void {
    const button = event.currentTarget as HTMLElement
    const operation = button.dataset.operation

    if (!operation || !isOperation(operation)) {
      return
    }

    this.session.operation = operation
    this.session.inputFormat = this.formatForOperation(operation, "input") as InputFormat
    this.session.outputFormat = this.formatForOperation(operation, "output") as OutputFormat
    this.session.outputText = null
    this.session.diagnostics = []
    this.session.warnings = []
    this.session.status = this.session.inputText.trim() ? "editing" : "empty"
    this.render()
  }

  selectOutputStyle(event: Event): void {
    const style = (event.currentTarget as HTMLSelectElement).value

    if (style !== "pretty" && style !== "compact") {
      return
    }

    this.session.outputStyle = style as JsonOutputStyle
    this.session.outputText = null
    this.session.diagnostics = []
    this.session.warnings = []
    this.session.status = this.session.inputText.trim() ? "editing" : "empty"
    this.render()
  }

  sample(): void {
    this.session.inputText =
      this.session.operation === "yaml-to-json" ? SAMPLE_YAML : SAMPLE_JSON
    this.session.outputText = null
    this.session.diagnostics = []
    this.session.warnings = []
    this.session.status = "editing"
    this.render()
  }

  reset(): void {
    this.requestId = null
    this.session = createEmptySession(this.modeConfig())
    this.render()
    this.inputTarget.focus()
  }

  process(event: Event): void {
    event.preventDefault()

    if (!this.worker) {
      this.session.status = "error"
      this.session.diagnostics = [
        {
          severity: "error",
          code: "worker-unavailable",
          message: "The browser worker is unavailable. Reload the page and try again.",
          offset: null,
          length: null,
          line: null,
          column: null,
        },
      ]
      this.render()
      return
    }

    const requestId = this.newRequestId()
    this.requestId = requestId
    this.session.isBusy = true
    this.session.status = "processing"
    this.session.outputText = null
    this.session.diagnostics = []
    this.session.warnings = []
    this.render()

    this.worker.postMessage({
      requestId,
      operation: this.session.operation,
      input: this.session.inputText,
      inputBytes: new TextEncoder().encode(this.session.inputText).byteLength,
      outputStyle: this.session.outputStyle,
    })
  }

  async copy(): Promise<void> {
    if (!this.session.outputText) {
      return
    }

    try {
      await navigator.clipboard.writeText(this.session.outputText)
    } catch {
      const temporary = document.createElement("textarea")
      temporary.value = this.session.outputText
      temporary.setAttribute("readonly", "")
      temporary.style.position = "fixed"
      temporary.style.opacity = "0"
      document.body.appendChild(temporary)
      temporary.select()
      document.execCommand("copy")
      temporary.remove()
    }

    this.copyStatusTarget.textContent = this.copyText("result.copy_success")
    trackEvent({
      name: "result_copied",
      operation: this.session.operation,
      ...(this.session.outputFormat !== "none"
        ? { output_format: this.session.outputFormat }
        : {}),
      outcome: this.session.status === "warning" ? "warning" : "success",
    })
  }

  download(): void {
    if (!this.session.outputText || this.session.outputFormat === "none") {
      return
    }

    const extension = this.session.outputFormat
    const blob = new Blob([this.session.outputText], {
      type: extension === "json" ? "application/json" : "text/yaml",
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "formatloom-result." + extension
    link.click()
    URL.revokeObjectURL(url)

    trackEvent({
      name: "result_downloaded",
      operation: this.session.operation,
      output_format: extension,
      outcome: this.session.status === "warning" ? "warning" : "success",
    })
  }

  private createWorker(): Worker | null {
    try {
      const worker = new Worker(this.workerSrcValue || "/assets/workbench/worker.js", {
        type: "module",
      })
      worker.addEventListener("message", (event: MessageEvent<ProcessingResponse>) => {
        this.receive(event.data)
      })
      worker.addEventListener("error", () => {
        if (!this.requestId) {
          return
        }

        this.session.isBusy = false
        this.session.status = "error"
        this.session.diagnostics = [
          {
            severity: "error",
            code: "worker-error",
            message: "The browser worker stopped unexpectedly. Reload and try again.",
            offset: null,
            length: null,
            line: null,
            column: null,
          },
        ]
        this.render()
      })
      return worker
    } catch {
      return null
    }
  }

  private receive(response: ProcessingResponse): void {
    if (!isCurrentRequest(this.requestId, response.requestId)) {
      return
    }

    this.session.isBusy = false
    this.session.outputText = response.output
    this.session.diagnostics = response.diagnostics
    this.session.warnings = response.warnings
    this.session.status = response.status
    this.render()

    const outputFormat = this.session.outputFormat
    const eventName = response.status === "error" ? "job_error" : "job_completed"
    trackEvent(
      operationEvent(
        eventName,
        this.session.operation,
        this.session.inputFormat,
        outputFormat,
        response.status,
      ),
    )
  }

  private render(): void {
    if (this.inputTarget.value !== this.session.inputText) {
      this.inputTarget.value = this.session.inputText
    }

    this.outputTarget.value = this.session.outputText ?? ""
    this.statusTarget.textContent = this.copyText(`status.${this.session.status}`)
    this.statusTarget.dataset.state = this.session.status
    this.inputBytesTarget.textContent = this.formatBytes(
      new TextEncoder().encode(this.session.inputText).byteLength,
    )
    this.operationLabelTarget.textContent = this.operationLabel(this.session.operation)
    this.formatHintTarget.textContent = this.formatHint(this.session.inputFormat)
    if (this.hasOutputStyleTarget) {
      this.outputStyleTarget.value = this.session.outputStyle
      this.outputStyleTarget.disabled = this.session.isBusy
    }
    this.validationResultTarget.hidden =
      this.session.operation !== "validate-json" || this.session.status !== "success"
    this.copyStatusTarget.textContent = ""

    this.renderMessages(this.diagnosticsTarget, this.session.diagnostics)
    this.renderMessages(this.warningsTarget, this.session.warnings)

    const hasOutput = Boolean(this.session.outputText)
    this.runButtonTarget.disabled = this.session.isBusy
    this.copyButtonTarget.disabled = !hasOutput || this.session.isBusy
    this.downloadButtonTarget.disabled =
      !hasOutput || this.session.isBusy || this.session.outputFormat === "none"

    for (const button of this.operationButtonTargets) {
      const active = button.dataset.operation === this.session.operation
      button.setAttribute("aria-pressed", String(active))
      button.classList.toggle("is-active", active)
    }
  }

  private renderMessages(
    target: HTMLElement,
    messages: Array<Diagnostic | ConversionWarning>,
  ): void {
    target.replaceChildren()

    if (messages.length === 0) {
      target.hidden = true
      return
    }

    target.hidden = false
    const list = document.createElement("ul")
    list.className = "message-list"

    for (const item of messages) {
      const entry = document.createElement("li")
      const location =
        item.line && item.column
          ? this.copyText("location", { line: item.line, column: item.column })
          : ""
      entry.textContent = this.messageFor(item) + location
      list.appendChild(entry)
    }

    target.appendChild(list)
  }

  private modeConfig(): WorkbenchModeConfig {
    return {
      key: this.modeValue as RouteMode,
      label: this.operationLabel(this.operationValue as Operation),
      operation: this.operationValue as Operation,
      inputFormat: this.inputFormatValue as InputFormat,
      outputFormat: this.outputFormatValue as OutputFormat,
    }
  }

  private formatForOperation(
    operation: Operation,
    direction: "input" | "output",
  ): InputFormat | OutputFormat {
    if (operation === "yaml-to-json") {
      return direction === "input" ? "yaml" : "json"
    }

    if (operation === "json-to-yaml") {
      return direction === "input" ? "json" : "yaml"
    }

    return direction === "input" ? "json" : "json"
  }

  private operationLabel(operation: Operation): string {
    switch (operation) {
      case "format-json":
        return this.copyText("operation.format_json")
      case "validate-json":
        return this.copyText("operation.validate_json")
      case "json-to-yaml":
        return this.copyText("operation.json_to_yaml")
      case "yaml-to-json":
        return this.copyText("operation.yaml_to_json")
    }
  }

  private formatHint(format: InputFormat): string {
    return format === "json"
      ? this.copyText("input.hint_json")
      : this.copyText("input.hint_yaml")
  }

  private messageFor(item: Diagnostic | ConversionWarning): string {
    const locale = this.locale()
    const key = "severity" in item
      ? `diagnostic.${item.code}`
      : `warning.${item.code === "duplicate-key" ? `duplicate-key.${item.sourceFormat}` : item.code.startsWith("yaml-parser-") ? "yaml-parser-warning" : item.code}`
    const translated = translate(locale, key)
    return translated === key ? item.message : translated
  }

  private copyText(
    key: string,
    variables: Record<string, string | number> = {},
  ): string {
    return translate(this.locale(), key, variables)
  }

  private locale(): Locale {
    return currentLocale(document)
  }

  private formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return bytes + " B"
    }
    return (bytes / 1024).toFixed(bytes < 10240 ? 1 : 0) + " KB"
  }

  private newRequestId(): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID()
    }
    return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2)
  }
}
